import React, { useState, useCallback } from "react";
import {
  Box,
  Paper,
  Button,
  Typography,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EmailIcon from "@mui/icons-material/Email";
import { CircularProgress } from "@mui/material";
import RestoreIcon from "@mui/icons-material/Restore";
import fileSaver from "file-saver";
import { FormatDateTime } from "../../../utils/formateDate";

function ReportItemDashboard({
  report: propReport,
  typeActivite,
  nomEmplacement,
  username,
  date,
  dateEvent,
  setReloadReports,
}) {
  const { t } = useTranslation(["Articles", "Common"]);

  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const userState = useSelector((state) => state.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isValidReport =
    !!propReport?.idActivite && !!propReport?.idTypeActivite;

  const handleDownloadClick = useCallback(async () => {
    if (!isValidReport) return;

    try {
      setLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Report/DownloadReportPdf/${propReport.idActivite}/${propReport.idTypeActivite}/${userState.lang}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );

      if (!response.ok) throw new Error("Erreur lors du téléchargement");

      const blob = await response.blob();

      fileSaver.saveAs(
        blob,
        `${propReport.idActivite}_${
          userState.lang === "EN"
            ? propReport.titleRepportEn
            : propReport.titleRepportFr
        }.pdf`,
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [isValidReport, propReport, userState.lang, userState.token]);

  const handleReopen = useCallback(async () => {
    if (!isValidReport) return;
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/Report/ReopenReport`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userState.token}`,
        },
        body: JSON.stringify({ id: propReport.idActivite }),
      });
      if (setReloadReports) setReloadReports(true);
      setOpenSnackbar(true);
    } catch (err) {
      console.error(err);
    }
  }, [isValidReport, propReport, setReloadReports, userState.token]);

  const handleEmailReport = useCallback(async () => {
    if (!isValidReport) return;

    try {
      setEmailLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Report/EmailReport/${propReport.idActivite}/${userState.lang}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Erreur lors de l'envoi du courriel");
      }

      if (setReloadReports) {
        await setReloadReports();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setEmailLoading(false);
    }
  }, [
    isValidReport,
    propReport.idActivite,
    userState.lang,
    userState.token,
    setReloadReports,
  ]);

  return (
    <>
      <Paper
        elevation={2}
        sx={{
          p: 1,
          borderRadius: 2,
          backgroundColor: "#f9f9f9",
          width: "100%",
          overflowX: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {typeActivite}
            </Typography>
            <Typography variant="body2">{nomEmplacement}</Typography>
            <Typography variant="body2" fontWeight="bold">
              {username}
            </Typography>
            <Typography variant="caption">
              {FormatDateTime(dateEvent)}
            </Typography>
            <Typography variant="caption">{FormatDateTime(date)}</Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              mt: isMobile ? 1 : 0,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {userState.roles.some(
              (role) => role === "AdminTI" || role === "ReportAdmin",
            ) && (
              <Tooltip title={t("Reopen")}>
                <span>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handleReopen}
                    disabled={!isValidReport}
                    size="small"
                    sx={{
                      minWidth: 36,
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      p: 0,
                    }}
                  >
                    <RestoreIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>
            )}
            {/* {propReport.emailAllowed && ( */}
            {propReport.emailAllowed && userState.roles.includes("AdminTI") && (
              <Tooltip
                title={
                  propReport.alreadySent
                    ? userState.lang === "FR"
                      ? "Courriel déjà envoyé"
                      : "Email already sent"
                    : userState.lang === "FR"
                      ? "Envoyer par courriel"
                      : "Email Report"
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color={propReport.alreadySent ? "success" : "primary"}
                    onClick={handleEmailReport}
                    disabled={emailLoading || !isValidReport}
                    size="small"
                    sx={{
                      minWidth: 36,
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      p: 0,
                    }}
                  >
                    {emailLoading ? (
                      <CircularProgress size={18} />
                    ) : (
                      <EmailIcon fontSize="small" />
                    )}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Tooltip title={t("Download")}>
              <span>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleDownloadClick}
                  disabled={loading || !isValidReport}
                  size="small"
                  sx={{
                    minWidth: 36,
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    p: 0,
                  }}
                >
                  <PictureAsPdfIcon fontSize="small" />
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {t("Report reopened successfully!")}
        </Alert>
      </Snackbar>
    </>
  );
}

export default React.memo(ReportItemDashboard);
